import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { find, get } from 'lodash';

import { AH$RouterLocation } from 'types';
import thematize from 'lib/thematize';
import { useDebounce, useDispatch, useSelector } from 'hooks';
import { NavigateFunction } from 'hooks/useNavigate';
import { getCompanyFormData } from 'slices/adminPanel';
import messages from 'components/AdminPanel/Companies/messages';
import formMessages from 'components/AdminPanel/Company/CompanyForm/messages';
import Reset from 'components/AdminPanel/Misc/Reset';
import Input from 'components/Base/Input';
import Text, { Is } from 'components/Base/Text';
import Select from 'components/Select';
import styles from './Filters.scss';

const theme = thematize(styles);

const defaultFilterOption = {
  value: undefined,
  label: <FormattedMessage {...messages.all} />,
};

const activityFilterOptions = [
  defaultFilterOption,
  {
    value: 'true',
    label: <FormattedMessage {...messages.active} />,
  },
];

const accessTypeFilterOptions = [
  defaultFilterOption,
  {
    value: 'pay',
    label: <FormattedMessage {...formMessages.pay} />,
  },
  {
    value: 'demo',
    label: <FormattedMessage {...formMessages.demo} />,
  },
  {
    value: 'ext',
    label: <FormattedMessage {...formMessages.ext} />,
  },
];

interface Props {
  location: AH$RouterLocation;
  navigate: NavigateFunction;
}

const Filters = ({ location, navigate }: Props): ReactElement => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [search, setSearch] = useState(location.query.search || '');
  const debouncedSearch = useDebounce(search);

  const companyFormData = useSelector(({ adminPanel }) => adminPanel.companyFormData);
  const accountManagerFilterOptions = companyFormData.data
    ? [
        defaultFilterOption,
        ...companyFormData.data.salesManagers.map(({ id, name }) => ({
          value: String(id),
          label: name,
        })),
      ]
    : [defaultFilterOption];
  const industryFilterOptions = companyFormData.data
    ? [
        defaultFilterOption,
        ...Object.keys(companyFormData.data.industry).map((id) => ({
          value: id,
          label: get(companyFormData.data, ['industry', id]),
        })),
      ]
    : [defaultFilterOption];

  useEffect(() => {
    if (!companyFormData.data) {
      dispatch(getCompanyFormData());
    }
  }, [companyFormData.data, dispatch]);

  useEffect(() => {
    if (debouncedSearch || location.query.search) {
      navigate({
        query: {
          ...location.query,
          offset: 0,
          search: debouncedSearch || undefined,
        },
      });
    }
  }, [debouncedSearch]);

  const handleChange = (filter: string, value: string | undefined): void => {
    navigate({
      query: {
        ...location.query,
        offset: 0,
        [filter]: value,
      },
    });
  };
  const handleReset = () => {
    navigate({
      query: {},
    });
    setSearch('');
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <div className={theme('container')}>
      <Reset onReset={handleReset} />
      <Input
        className={theme('search')}
        placeholder={formatMessage(messages.search)}
        value={search}
        onChange={handleSearch}
      />
      <div className={theme('filter')}>
        <Text is={Is.Strong}>
          <FormattedMessage {...messages.activity} />
        </Text>
        <Select
          isSearchable={false}
          options={activityFilterOptions}
          value={find(activityFilterOptions, { value: location.query.only_active })}
          onChange={({ value }: { value: string | undefined }): void =>
            handleChange('only_active', value)
          }
        />
      </div>
      <div className={theme('filter')}>
        <Text is={Is.Strong}>
          <FormattedMessage {...formMessages.access_type} />
        </Text>
        <Select
          isSearchable={false}
          options={accessTypeFilterOptions}
          value={find(accessTypeFilterOptions, { value: location.query.access_type })}
          onChange={({ value }: { value: string | undefined }): void =>
            handleChange('access_type', value)
          }
        />
      </div>
      <div className={theme('filter')}>
        <Text is={Is.Strong}>
          <FormattedMessage {...formMessages.account_manager} />
        </Text>
        <Select
          options={accountManagerFilterOptions}
          value={find(accountManagerFilterOptions, { value: location.query.account_manager })}
          onChange={({ value }: { value: string | undefined }): void =>
            handleChange('account_manager', value)
          }
        />
      </div>
      <div className={theme('filter')}>
        <Text is={Is.Strong}>
          <FormattedMessage {...formMessages.industry} />
        </Text>
        <Select
          isSearchable={false}
          options={industryFilterOptions}
          value={find(industryFilterOptions, { value: location.query.industry })}
          onChange={({ value }: { value: string | undefined }): void =>
            handleChange('industry', value)
          }
        />
      </div>
    </div>
  );
};

export default Filters;
